import React from 'react';
import {
  LineChart as RechartsLineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';
import type { LabeledLineChartViz } from 'shared/src/api/ai-labeled-viz-types';
import type { ValueFormat } from 'shared/src/api/ai-viz-types';
import { transformLineChart, formatVizValue } from 'shared/src/utils/ai/ai-viz-transformers';
import { formatDate } from 'shared/src/date-string-utils/date-utils';
import type { CubeRow } from './chart-utils';
import { CHART_PALETTE, TOOLTIP_STYLE } from './chart-utils';

type LineChartProps = {
  rows: CubeRow[];
  viz: LabeledLineChartViz;
};

export function LineChart({ rows, viz }: LineChartProps) {
  const data = transformLineChart(rows, viz);
  const { lineKeys } = viz.mapping;
  const { lineLabels, axisFormat } = viz.labels;

  const hasData = data.some(row => lineKeys.some(k => row[k] !== null && row[k] !== undefined));

  if (!hasData) {
    return (
      <div className="w-full bg-white rounded-2xl shadow-lg p-6">
        <h2 className="text-2xl font-semibold mb-4">{viz.title}</h2>
        <div className="flex items-center justify-center" style={{ height: 450 }}>
          <p className="text-gray-400 text-lg">No data available</p>
        </div>
      </div>
    );
  }

  const tickInterval = Math.max(1, Math.floor(data.length / 12));

  return (
    <div className="w-full bg-white rounded-2xl shadow-lg p-6">
      <h2 className="text-2xl font-semibold mb-4">{viz.title}</h2>

      <ResponsiveContainer width="100%" height={450}>
        <RechartsLineChart data={data} margin={{ top: 8, right: 24, left: 8, bottom: 8 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />

          <XAxis
            dataKey="date"
            tickFormatter={formatDateTick}
            interval={tickInterval}
            tick={{ fontSize: 12 }}
          />

          <YAxis
            tickFormatter={(v: number) => formatVizValue(v, axisFormat)}
            tick={{ fontSize: 12 }}
          />

          <Tooltip
            content={<CustomTooltip lineLabels={lineLabels} axisFormat={axisFormat} />}
            isAnimationActive={false}
          />

          <Legend wrapperStyle={{ paddingTop: 6, fontSize: 12 }} iconSize={10} />

          {lineKeys.map((key, i) => (
            <Line
              key={key}
              type="linear"
              dataKey={key}
              name={lineLabels[key] ?? key}
              stroke={CHART_PALETTE[i % CHART_PALETTE.length]}
              strokeWidth={2}
              dot={false}
              isAnimationActive
              animationDuration={1200}
              animationEasing="ease-in-out"
              connectNulls
            />
          ))}
        </RechartsLineChart>
      </ResponsiveContainer>
    </div>
  );
}

// ─── Internal helpers (in call order) ───

function formatDateTick(iso: string): string {
  const d = new Date(iso + 'T00:00:00');
  return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short' });
}

function CustomTooltip({
  active,
  payload,
  label,
  lineLabels,
  axisFormat
}: {
  active?: boolean;
  payload?: Array<{ dataKey: string; value: number; color: string }>;
  label?: string;
  lineLabels: Record<string, string>;
  axisFormat: ValueFormat;
}) {
  if (!active || !payload?.length) return null;

  return (
    <div style={TOOLTIP_STYLE}>
      <p style={{ marginBottom: 6, fontWeight: 600 }}>
        {label ? formatDate(label, 'MMMM d, yyyy') : ''}
      </p>
      {payload.map(entry => (
        <p key={entry.dataKey} style={{ margin: '2px 0', color: entry.color }}>
          {lineLabels[entry.dataKey] ?? entry.dataKey}: {formatVizValue(entry.value, axisFormat)}
        </p>
      ))}
    </div>
  );
}
